import React, { useState, useCallback, useEffect } from 'react';
import { db } from '../firebase/config.js';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { 
  isBefore, 
  startOfDay, 
  parseISO, 
  areIntervalsOverlapping,
  differenceInDays,
  eachDayOfInterval,
  format
} from 'date-fns';
import { getAuth } from "firebase/auth";
import TenantConfigService from '../services/TenantConfigService.js';
import { isSchoolHoliday } from '../utils/scheduleUtils.js';

const auth = getAuth();

const RequestAbsence = ({ currentUser }) => {
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [needsSubCoverage, setNeedsSubCoverage] = useState(false);
  const navigate = useNavigate();

  // OFF-CAMPUS FEATURE STATES
  const [isOffCampusOnly, setIsOffCampusOnly] = useState(false);
  const [departureTime, setDepartureTime] = useState('');
  const [returnTime, setReturnTime] = useState('');

  // HALF-DAY FEATURE STATES
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState('AM'); // AM or PM

  // Holiday and configuration state
  const [tenantConfig, setTenantConfig] = useState({
    schoolHolidays: []
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [dateValidationInfo, setDateValidationInfo] = useState({
    startDateInfo: null,
    endDateInfo: null,
    businessDaysCount: 0,
    hasInvalidDates: false
  });

  // BLACKOUT DATES STATE
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [blackoutWarning, setBlackoutWarning] = useState(null);
  const [acknowledgeBlackout, setAcknowledgeBlackout] = useState(false);

  const reasonOptions = [
    'Work-Related',
    'Sickness',
    'Personal Leave',
    'Vacation',
    'Other'
  ];

  const offCampusReasonOptions = [
    'Professional Development',
    'Field Trip',
    'Meeting',
    'Doctor Appointment',
    'Personal Errand',
    'Other'
  ];

  // Check if dates fall within blackout periods
  const checkBlackoutDates = useCallback((startDate, endDate, blackouts) => {
    console.log('🔍 DEBUG checkBlackoutDates called with:', { startDate, endDate, blackoutsCount: blackouts?.length });
    
    if (!startDate || !endDate || !blackouts || blackouts.length === 0) {
      console.log('🔍 DEBUG: Early return - missing params or no blackouts');
      return null;
    }

    const requestStart = new Date(startDate + 'T00:00:00');
    const requestEnd = new Date(endDate + 'T23:59:59');
    
    console.log('🔍 DEBUG: Request range:', { requestStart, requestEnd });

    // Check each blackout period
    for (const blackout of blackouts) {
      console.log('🔍 DEBUG: Checking blackout:', blackout);
      
      if (!blackout.enabled) {
        console.log('🔍 DEBUG: Skipping disabled blackout:', blackout.name);
        continue;
      }

      if (blackout.type === 'single') {
        const blackoutDate = new Date(blackout.date + 'T00:00:00');
        console.log('🔍 DEBUG: Checking single date:', { blackoutDate, requestStart, requestEnd });
        
        // Check if blackout date falls within request range
        if (blackoutDate >= requestStart && blackoutDate <= requestEnd) {
          console.log('⚠️ MATCH FOUND: Single date blackout overlaps!');
          return {
            name: blackout.name,
            type: 'single',
            date: blackout.date,
            description: blackout.description
          };
        }
      } else if (blackout.type === 'range') {
        const blackoutStart = new Date(blackout.startDate + 'T00:00:00');
        const blackoutEnd = new Date(blackout.endDate + 'T23:59:59');
        
        console.log('🔍 DEBUG: Checking range:', { blackoutStart, blackoutEnd, requestStart, requestEnd });
        
        // Check if there's any overlap between request range and blackout range
        const hasOverlap = (requestStart <= blackoutEnd && requestEnd >= blackoutStart);
        
        console.log('🔍 DEBUG: Overlap check result:', hasOverlap);
        
        if (hasOverlap) {
          console.log('⚠️ MATCH FOUND: Range blackout overlaps!');
          return {
            name: blackout.name,
            type: 'range',
            startDate: blackout.startDate,
            endDate: blackout.endDate,
            description: blackout.description
          };
        }
      }
    }

    console.log('✅ No blackout matches found');
    return null;
  }, []);

  // Load tenant configuration including school holidays and blackout dates
  const loadTenantConfig = useCallback(async () => {
    if (!currentUser?.tenantId) {
      console.warn('RequestAbsence: Cannot load config, tenantId is missing.');
      return;
    }

    setIsLoadingConfig(true);
    try {
      // Load school holidays and blackout dates in parallel
      const [schoolHolidaysConfig, blackoutConfig] = await Promise.all([
        TenantConfigService.getConfig(
          currentUser.tenantId, 
          'school_holidays', 
          { holidays: [] }
        ),
        TenantConfigService.getConfig(
          currentUser.tenantId,
          'blackout_dates',
          { blackoutDates: [] }
        )
      ]);

      const schoolHolidays = schoolHolidaysConfig.holidays || [];
      const blackoutDatesData = blackoutConfig.blackoutDates || [];
      
      console.log('🔍 DEBUG: Raw blackout config from Firestore:', blackoutConfig);
      console.log('🔍 DEBUG: Blackout dates array:', blackoutDatesData);
      console.log('🔍 DEBUG: Blackout dates length:', blackoutDatesData.length);
      
      // Only load enabled blackout dates
      const enabledBlackouts = blackoutDatesData.filter(b => b.enabled);
      
      console.log('🔍 DEBUG: Enabled blackouts after filter:', enabledBlackouts);
      console.log('🔍 DEBUG: Number of enabled blackouts:', enabledBlackouts.length);

      setTenantConfig({
        schoolHolidays
      });
      
      setBlackoutDates(enabledBlackouts);

      console.log('✅ Loaded school holidays for absence requests:', schoolHolidays);
      console.log('✅ Loaded blackout dates for absence requests:', enabledBlackouts);
    } catch (error) {
      console.error('Error loading tenant configuration for absence requests:', error);
      setError('Failed to load system configuration. Some date validations may not work properly.');
    } finally {
      setIsLoadingConfig(false);
    }
  }, [currentUser?.tenantId]);

  useEffect(() => {
    if (currentUser && currentUser.tenantId) {
      loadTenantConfig();
    }
  }, [currentUser, loadTenantConfig]);

  // Check for blackout dates whenever dates change
  useEffect(() => {
    console.log('🔍 DEBUG: Date change detected');
    console.log('🔍 DEBUG: startDate:', startDate);
    console.log('🔍 DEBUG: endDate:', endDate);
    console.log('🔍 DEBUG: isOffCampusOnly:', isOffCampusOnly);
    console.log('🔍 DEBUG: blackoutDates array:', blackoutDates);
    
    if (startDate && endDate && !isOffCampusOnly) {
      console.log('🔍 DEBUG: Checking for blackout dates...');
      const blackoutInfo = checkBlackoutDates(startDate, endDate, blackoutDates);
      console.log('🔍 DEBUG: Blackout check result:', blackoutInfo);
      
      setBlackoutWarning(blackoutInfo);
      
      // Reset acknowledgment if blackout changes
      if (blackoutInfo) {
        console.log('⚠️ BLACKOUT DETECTED! Setting warning...');
        setAcknowledgeBlackout(false);
      } else {
        console.log('✅ No blackout detected for these dates');
      }
    } else {
      console.log('🔍 DEBUG: Skipping blackout check (missing dates or off-campus)');
      setBlackoutWarning(null);
      setAcknowledgeBlackout(false);
    }
  }, [startDate, endDate, blackoutDates, isOffCampusOnly, checkBlackoutDates]);

  // Check if a date is a weekend
  const isWeekend = (dateString) => {
    const date = new Date(dateString + 'T12:00:00');
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  };

  // Check if a date is invalid (weekend or holiday)
  const isInvalidAbsenceDate = useCallback((dateString) => {
    // For off-campus notifications, weekends and holidays are allowed
    if (isOffCampusOnly) {
      return { isInvalid: false };
    }
    
    if (isWeekend(dateString)) {
      return { isInvalid: true, reason: 'weekend', message: 'Weekends are not work days' };
    }
    
    const holidayCheck = isSchoolHoliday(dateString, tenantConfig.schoolHolidays);
    if (holidayCheck.isHoliday) {
      return { 
        isInvalid: true, 
        reason: 'holiday', 
        message: `School Holiday: ${holidayCheck.holidayName}`,
        holidayInfo: holidayCheck
      };
    }
    
    return { isInvalid: false };
  }, [tenantConfig.schoolHolidays, isOffCampusOnly]);

  // Calculate business days (excluding weekends and holidays) in a date range
  const calculateBusinessDays = useCallback((start, end, isHalfDayRequest = false) => {
    if (!start || !end) return 0;
    
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    
    if (isBefore(endDate, startDate)) return 0;
    
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    
    let businessDays = 0;
    allDays.forEach(day => {
      const dateString = format(day, 'yyyy-MM-dd');
      const validation = isInvalidAbsenceDate(dateString);
      if (!validation.isInvalid) {
        businessDays++;
      }
    });
    
    // For half-day requests, return 0.5 if it's a single day
    if (isHalfDayRequest && start === end && businessDays === 1) {
      return 0.5;
    }
    
    return businessDays;
  }, [isInvalidAbsenceDate]);

  // Update date validation info when dates change
  const updateDateValidationInfo = useCallback(() => {
    const info = {
      startDateInfo: null,
      endDateInfo: null,
      businessDaysCount: 0,
      hasInvalidDates: false
    };

    if (startDate) {
      info.startDateInfo = isInvalidAbsenceDate(startDate);
      if (info.startDateInfo.isInvalid) {
        info.hasInvalidDates = true;
      }
    }

    if (endDate) {
      info.endDateInfo = isInvalidAbsenceDate(endDate);
      if (info.endDateInfo.isInvalid) {
        info.hasInvalidDates = true;
      }
    }

    if (startDate && endDate && !info.hasInvalidDates) {
      info.businessDaysCount = calculateBusinessDays(startDate, endDate, isHalfDay);
    }

    setDateValidationInfo(info);
  }, [startDate, endDate, isInvalidAbsenceDate, calculateBusinessDays, isHalfDay]);

  useEffect(() => {
    if (!isLoadingConfig) {
      updateDateValidationInfo();
    }
  }, [startDate, endDate, isLoadingConfig, isOffCampusOnly, isHalfDay, updateDateValidationInfo]);

  // Check if user is eligible for substitute coverage option
  const isEligibleForSubCoverage = () => {
    if (!currentUser?.role) return false;
    
    const eligibleRoles = [
      'faculty/staff', 
      'Faculty/Staff', 
      'Teacher', 
      'Educational Administrator', 
      'Educational Admin',
      'System Administrator',
      'Sys Admin'
    ];
    
    return eligibleRoles.some(role => 
      currentUser.role.toLowerCase().includes(role.toLowerCase()) ||
      role.toLowerCase().includes(currentUser.role.toLowerCase())
    );
  };

  const validateUserData = () => {
    if (!currentUser?.uid || !currentUser?.supervisorId) {
      return "Your user or supervisor information is incomplete. Please contact an administrator.";
    }
    if (!currentUser?.tenantId) {
      return "Your account is missing tenant information. Please contact your administrator.";
    }
    return null;
  };

  const validateFormData = () => {
    if (!startDate || !reason) {
      return "Please fill out all required fields: Date and Reason.";
    }

    // For off-campus only, end date is optional (single day assumed)
    if (!isOffCampusOnly && !endDate) {
      return "Please fill out all required fields: Start Date, End Date, and Reason.";
    }
    
    // Half-day validation
    if (isHalfDay && startDate !== endDate && endDate) {
      return "Half-day absences can only be requested for a single day. Please set the same start and end date.";
    }
    
    if (!isOffCampusOnly && isBefore(startOfDay(parseISO(endDate)), startOfDay(parseISO(startDate)))) {
      return "The end date cannot be before the start date.";
    }
    
    if (reason === 'Other' && !reasonOther.trim()) {
      return "Please specify a reason when 'Other' is selected.";
    }

    // For off-campus only, validate times
    if (isOffCampusOnly) {
      if (!departureTime || !returnTime) {
        return "Please specify departure and return times for off-campus notification.";
      }
      
      // If same day, check that return time is after departure time
      if (startDate === endDate || !endDate) {
        const [depHour, depMin] = departureTime.split(':').map(Number);
        const [retHour, retMin] = returnTime.split(':').map(Number);
        const depMinutes = depHour * 60 + depMin;
        const retMinutes = retHour * 60 + retMin;
        
        if (retMinutes <= depMinutes) {
          return "Return time must be after departure time for same-day off-campus notifications.";
        }
      }
    }

    // For regular absences, validate dates aren't weekends/holidays
    if (!isOffCampusOnly) {
      if (dateValidationInfo.hasInvalidDates) {
        if (dateValidationInfo.startDateInfo?.isInvalid) {
          return `Start date is invalid: ${dateValidationInfo.startDateInfo.message}`;
        }
        if (dateValidationInfo.endDateInfo?.isInvalid) {
          return `End date is invalid: ${dateValidationInfo.endDateInfo.message}`;
        }
      }

      if (dateValidationInfo.businessDaysCount === 0) {
        return "The selected date range contains no valid work days. Please select dates that include at least one business day.";
      }
      
      // BLACKOUT DATE VALIDATION
      if (blackoutWarning && !acknowledgeBlackout) {
        return "You must acknowledge that you might be at risk of being docked pay during the blackout period before submitting.";
      }
    }

    return null;
  };

  const checkForOverlappingAbsences = async () => {
    // Skip overlap check for off-campus notifications
    if (isOffCampusOnly) return null;
    
    const baseQuery = collection(db, "absence_requests");
    
    const pendingQuery = query(
      baseQuery,
      where("requesterUid", "==", currentUser.uid),
      where("status", "==", "pending"),
      where("isOffCampusOnly", "!=", true)  // Exclude off-campus notifications
    );

    const approvedQuery = query(
      baseQuery,
      where("requesterUid", "==", currentUser.uid),
      where("status", "==", "approved"),
      where("isOffCampusOnly", "!=", true)  // Exclude off-campus notifications
    );

    const [pendingSnapshot, approvedSnapshot] = await Promise.all([
      getDocs(pendingQuery),
      getDocs(approvedQuery)
    ]);

    const allExistingRequests = [...pendingSnapshot.docs, ...approvedSnapshot.docs];
    const newRequestInterval = {
      start: startOfDay(parseISO(startDate)),
      end: startOfDay(parseISO(endDate))
    };

    for (const doc of allExistingRequests) {
      const existing = doc.data();
      if (existing.startDate && existing.endDate) {
        const existingInterval = {
          start: startOfDay(parseISO(existing.startDate)),
          end: startOfDay(parseISO(existing.endDate))
        };

        if (areIntervalsOverlapping(newRequestInterval, existingInterval, { inclusive: true })) {
          return "This request overlaps with an existing absence. Please check MyRoster or contact your supervisor.";
        }
      }
    }
    return null;
  };

  const createAbsenceRequest = async () => {
    // Get fresh authentication token
    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
    const effectiveTenantId = idTokenResult.claims.tenantId || currentUser.tenantId;

    const actualEndDate = endDate || startDate; // Use start date if end date not provided

    const absenceRequestData = {
      userId: currentUser.uid,
      requesterUid: currentUser.uid,
      requesterDocId: currentUser.docId,
      requesterName: currentUser.name,
      requesterEmail: currentUser.email,
      tenantId: effectiveTenantId,
      supervisorId: currentUser.supervisorId,
      startDate: startDate,
      endDate: actualEndDate,
      reason: reason,
      reasonOther: reason === 'Other' ? reasonOther.trim() : '',
      notes: notes.trim(),
      status: isOffCampusOnly ? 'approved' : 'pending',  // Auto-approve off-campus notifications
      createdAt: serverTimestamp(),
      createdBy: currentUser.docId || currentUser.uid,
      createdByName: currentUser.name,
      createdOnBehalf: false,
      needsSubCoverage: needsSubCoverage,
      // Off-campus specific fields
      isOffCampusOnly: isOffCampusOnly,
      departureTime: isOffCampusOnly ? departureTime : null,
      returnTime: isOffCampusOnly ? returnTime : null,
      // Half-day specific fields
      isHalfDay: isHalfDay && !isOffCampusOnly, // Half-day only applies to regular absences
      halfDayPeriod: isHalfDay && !isOffCampusOnly ? halfDayPeriod : null,
      // NEW: Add requestType field for email routing
      requestType: isOffCampusOnly ? 'offCampus' : 'absence',
      isOffCampusNotification: isOffCampusOnly, // Keep for backward compatibility
      // Business days calculation
      businessDaysCount: isOffCampusOnly ? 0 : dateValidationInfo.businessDaysCount,
      totalDaysRequested: isHalfDay ? 0.5 : (differenceInDays(parseISO(actualEndDate), parseISO(startDate)) + 1),
      excludesWeekendsAndHolidays: !isOffCampusOnly,
      // BLACKOUT DATE INFORMATION
      isBlackoutPeriod: blackoutWarning !== null,
      blackoutInfo: blackoutWarning ? {
        name: blackoutWarning.name,
        type: blackoutWarning.type,
        acknowledgedNoPay: true,
        acknowledgedAt: new Date().toISOString()
      } : null,
      history: [
        {
          status: isOffCampusOnly ? 'approved' : 'pending',
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser.name,
          updatedById: currentUser.docId || currentUser.uid,
          note: isOffCampusOnly ? 'Auto-approved: Off-campus notification' : null
        }
      ]
    };

    // Development-only tenant override for testing
    //if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
    //  absenceRequestData.tenantId = '7ZOi506NtfzMEtsaXTK2'; // TMS tenant for testing
    //  console.log('Development mode: Using test tenant ID for absence request');
    //}

    const docRef = await addDoc(collection(db, 'absence_requests'), absenceRequestData);
    return docRef.id;
  };

  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'permission-denied':
        return "Permission denied. You may not have the required permissions to create absence requests. Please contact your administrator.";
      case 'invalid-argument':
        return "Invalid data provided. Please check all fields and try again.";
      case 'unauthenticated':
        return "Authentication error. Please sign out and sign back in.";
      default:
        return `An error occurred: ${error.message}`;
    }
  };

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setReason('');
    setReasonOther('');
    setNotes('');
    setNeedsSubCoverage(false);
    setIsOffCampusOnly(false);
    setDepartureTime('');
    setReturnTime('');
    setIsHalfDay(false);
    setHalfDayPeriod('AM');
    setAcknowledgeBlackout(false);
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    if (!isOffCampusOnly && (!endDate || isBefore(parseISO(endDate), parseISO(newStartDate)))) {
      setEndDate(newStartDate);
    }
    // If half-day is selected, ensure end date matches start date
    if (isHalfDay) {
      setEndDate(newStartDate);
    }
  };

  const handleOffCampusChange = (checked) => {
    setIsOffCampusOnly(checked);
    if (checked) {
      // Clear substitute coverage and half-day when switching to off-campus
      setNeedsSubCoverage(false);
      setIsHalfDay(false);
      // Set end date to match start date for single day
      if (startDate && !endDate) {
        setEndDate(startDate);
      }
    } else {
      // Clear time fields when switching to regular absence
      setDepartureTime('');
      setReturnTime('');
    }
  };

  const handleHalfDayChange = (checked) => {
    setIsHalfDay(checked);
    if (checked) {
      // For half-day, ensure end date matches start date
      if (startDate) {
        setEndDate(startDate);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      // Validate user data
      const userError = validateUserData();
      if (userError) {
        setError(userError);
        return;
      }

      // Validate form data (now includes blackout validation)
      const formError = validateFormData();
      if (formError) {
        setError(formError);
        return;
      }

      // Check for authentication
      if (!auth.currentUser) {
        setError("No authenticated user found. Please sign in again.");
        return;
      }

      // Check for overlapping absences
      const overlapError = await checkForOverlappingAbsences();
      if (overlapError) {
        setError(overlapError);
        return;
      }

      // Create the absence request
      await createAbsenceRequest();

      // Show success message
      const successText = isOffCampusOnly 
        ? "Off-campus notification submitted successfully! Administration has been notified."
        : needsSubCoverage
          ? "Absence request submitted! Redirecting to substitute request form..."
          : "Absence request submitted successfully! Your supervisor will be notified.";
      
      setSuccessMessage(successText);
      resetForm();

      // If substitute coverage needed, navigate after a short delay
      if (needsSubCoverage && !isOffCampusOnly) {
        setTimeout(() => {
          navigate('/substitute-request');
        }, 2000);
      } else {
        // For off-campus or regular requests, optionally redirect after showing success
        setTimeout(() => {
          navigate('/absence-history');
        }, 2500);
      }

    } catch (err) {
      console.error("Error submitting absence request:", err);
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while configuration is loading
  if (isLoadingConfig) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Request Time Off / Off-Campus</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>Loading system configuration...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we load your school's settings.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            Request Time Off / Off-Campus
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            Submit a request for an absence or notify administration when you'll be off-campus.
            {!isOffCampusOnly && (
              <>
                <br />
                <small style={{ color: '#856404', fontWeight: 'bold' }}>
                  Note: Weekends and school holidays are automatically excluded from absence calculations.
                </small>
              </>
            )}
          </p>
        </div>
        <div>
          <button
            type="button"
            onClick={() => navigate('/absence-history')}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '160px',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'background-color 0.2s, transform 0.1s',
              opacity: isSubmitting ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (!isSubmitting) e.target.style.backgroundColor = '#138496';
            }}
            onMouseOut={(e) => {
              if (!isSubmitting) e.target.style.backgroundColor = '#17a2b8';
            }}
          >
            📋 Absence History
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {/* Main Form */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        padding: '30px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
      }}>
        <form onSubmit={handleSubmit}>
          {/* Request Type Selection */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              Request Type
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {/* Regular Absence Option */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'start', 
                padding: '15px', 
                border: !isOffCampusOnly ? '2px solid #007bff' : '1px solid #ddd',
                borderRadius: '8px', 
                cursor: 'pointer',
                backgroundColor: !isOffCampusOnly ? '#e7f3ff' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  checked={!isOffCampusOnly}
                  onChange={() => handleOffCampusChange(false)}
                  style={{ marginRight: '10px', marginTop: '3px', cursor: 'pointer' }}
                />
                <div>
                  <strong style={{ fontSize: '16px', color: '#333' }}>📅 Time Off Request</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Request absence from work. Requires supervisor approval and may need substitute coverage.
                  </p>
                </div>
              </label>

              {/* Off-Campus Option */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'start', 
                padding: '15px', 
                border: isOffCampusOnly ? '2px solid #007bff' : '1px solid #ddd',
                borderRadius: '8px', 
                cursor: 'pointer',
                backgroundColor: isOffCampusOnly ? '#e7f3ff' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  checked={isOffCampusOnly}
                  onChange={() => handleOffCampusChange(true)}
                  style={{ marginRight: '10px', marginTop: '3px', cursor: 'pointer' }}
                />
                <div>
                  <strong style={{ fontSize: '16px', color: '#333' }}>🏢 Off-Campus Notification</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Notify administration that you'll be off campus for meetings, professional development, etc. 
                    Does not require supervisor approval.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Half-Day Option (only for regular absences) */}
          {!isOffCampusOnly && (
            <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '16px' }}>
                <input
                  type="checkbox"
                  checked={isHalfDay}
                  onChange={(e) => handleHalfDayChange(e.target.checked)}
                  style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <strong>Half-Day Absence</strong>
              </label>
              {isHalfDay && (
                <div style={{ marginTop: '15px', marginLeft: '28px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                    Which half of the day?
                  </label>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="halfDayPeriod"
                        value="AM"
                        checked={halfDayPeriod === 'AM'}
                        onChange={(e) => setHalfDayPeriod(e.target.value)}
                        style={{ marginRight: '5px' }}
                      />
                      Morning (AM)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="halfDayPeriod"
                        value="PM"
                        checked={halfDayPeriod === 'PM'}
                        onChange={(e) => setHalfDayPeriod(e.target.value)}
                        style={{ marginRight: '5px' }}
                      />
                      Afternoon (PM)
                    </label>
                  </div>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                    Half-day absences count as 0.5 days and are for a single day only.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Date Selection */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              {isOffCampusOnly ? 'Date and Time' : (isHalfDay ? 'Date' : 'Dates')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isOffCampusOnly ? 'repeat(auto-fit, minmax(200px, 1fr))' : '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  {isOffCampusOnly ? 'Date *' : (isHalfDay ? 'Date *' : 'Start Date *')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: dateValidationInfo.startDateInfo?.isInvalid && !isOffCampusOnly ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: dateValidationInfo.startDateInfo?.isInvalid && !isOffCampusOnly ? '#fff5f5' : 'white'
                  }}
                />
                {!isOffCampusOnly && dateValidationInfo.startDateInfo?.isInvalid && (
                  <div style={{ 
                    marginTop: '5px', 
                    fontSize: '14px', 
                    color: '#dc3545', 
                    fontWeight: 'bold' 
                  }}>
                    ⚠️ {dateValidationInfo.startDateInfo.message}
                  </div>
                )}
              </div>
              
              {!isOffCampusOnly && !isHalfDay && (
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required={!isOffCampusOnly && !isHalfDay}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: dateValidationInfo.endDateInfo?.isInvalid ? '2px solid #dc3545' : '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                      backgroundColor: dateValidationInfo.endDateInfo?.isInvalid ? '#fff5f5' : 'white'
                    }}
                  />
                  {dateValidationInfo.endDateInfo?.isInvalid && (
                    <div style={{ 
                      marginTop: '5px', 
                      fontSize: '14px', 
                      color: '#dc3545', 
                      fontWeight: 'bold' 
                    }}>
                      ⚠️ {dateValidationInfo.endDateInfo.message}
                    </div>
                  )}
                </div>
              )}

              {/* Time fields for off-campus */}
              {isOffCampusOnly && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                      Departure Time *
                    </label>
                    <input
                      type="time"
                      value={departureTime}
                      onChange={(e) => setDepartureTime(e.target.value)}
                      required={isOffCampusOnly}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                      Return Time *
                    </label>
                    <input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      required={isOffCampusOnly}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            
            {/* Business days summary (only for regular absences) */}
            {!isOffCampusOnly && startDate && endDate && !dateValidationInfo.hasInvalidDates && dateValidationInfo.businessDaysCount > 0 && (
              <div style={{ 
                marginTop: '15px', 
                padding: '12px', 
                backgroundColor: '#d1ecf1', 
                border: '1px solid #bee5eb', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontWeight: 'bold', color: '#0c5460', marginBottom: '5px' }}>
                  Absence Summary
                </div>
                <div style={{ fontSize: '14px', color: '#0c5460' }}>
                  <strong>Business Days Requested:</strong> {dateValidationInfo.businessDaysCount}
                  {isHalfDay && <span style={{ marginLeft: '10px' }}>(Half-day {halfDayPeriod})</span>}
                  {startDate !== endDate && !isHalfDay && (
                    <span style={{ marginLeft: '15px' }}>
                      <strong>Total Calendar Days:</strong> {differenceInDays(parseISO(endDate), parseISO(startDate)) + 1}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                  Weekends and holidays are automatically excluded from your absence request.
                </div>
              </div>
            )}
            
            {/* BLACKOUT DATE WARNING */}
            {!isOffCampusOnly && blackoutWarning && (
              <div style={{ 
                marginTop: '15px',
                padding: '20px',
                backgroundColor: '#fff3cd',
                border: '3px solid #ffc107',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(255, 193, 7, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '15px' }}>
                  <div style={{ fontSize: '40px', lineHeight: 1 }}>⚠️</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: '0 0 10px 0', 
                      color: '#856404', 
                      fontSize: '20px', 
                      fontWeight: 'bold' 
                    }}>
                      BLACKOUT PERIOD WARNING
                    </h3>
                    
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8d7da', 
                      border: '2px solid #dc3545', 
                      borderRadius: '6px',
                      marginBottom: '15px'
                    }}>
                      <p style={{ 
                        margin: '0 0 10px 0', 
                        color: '#721c24', 
                        fontSize: '16px', 
                        fontWeight: 'bold',
                        lineHeight: '1.5'
                      }}>
                        Your requested dates overlap with a <strong>BLACKOUT PERIOD</strong> where PTO is not recommended:
                      </p>
                      <div style={{ 
                        padding: '10px', 
                        backgroundColor: 'white', 
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        marginBottom: '10px'
                      }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
                          📅 {blackoutWarning.name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {blackoutWarning.type === 'single' ? (
                            <span>Date: <strong>{blackoutWarning.date}</strong></span>
                          ) : (
                            <span>
                              Dates: <strong>{blackoutWarning.startDate}</strong> to <strong>{blackoutWarning.endDate}</strong>
                            </span>
                          )}
                        </div>
                        {blackoutWarning.description && (
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#666', 
                            marginTop: '8px',
                            fontStyle: 'italic',
                            borderTop: '1px solid #ddd',
                            paddingTop: '8px'
                          }}>
                            {blackoutWarning.description}
                          </div>
                        )}
                      </div>
                      <p style={{ 
                        margin: '0', 
                        color: '#721c24', 
                        fontSize: '15px',
                        fontWeight: 'bold',
                        lineHeight: '1.6'
                      }}>
                        ⚠️ <strong>IMPORTANT:</strong> If you are absent during this blackout period, 
                        <span style={{ 
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          margin: '0 4px'
                        }}>
                          you might be at risk of being docked pay
                        </span>
                        for those days. Your supervisor will still need to approve this request, but 
                        any absence during blackout dates may result in docked pay.
                      </p>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6'
                    }}>
                      <input
                        type="checkbox"
                        id="blackout-acknowledge"
                        checked={acknowledgeBlackout}
                        onChange={(e) => setAcknowledgeBlackout(e.target.checked)}
                        style={{ 
                          width: '24px', 
                          height: '24px', 
                          cursor: 'pointer',
                          accentColor: '#ffc107'
                        }}
                      />
                      <label 
                        htmlFor="blackout-acknowledge" 
                        style={{ 
                          fontSize: '15px', 
                          fontWeight: 'bold', 
                          cursor: 'pointer',
                          color: '#333'
                        }}
                      >
                        I understand that I might be at risk of being docked pay for any days I am absent during this blackout period
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Warning for no business days */}
            {!isOffCampusOnly && startDate && endDate && !dateValidationInfo.hasInvalidDates && dateValidationInfo.businessDaysCount === 0 && (
              <div style={{ 
                marginTop: '15px', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '5px' }}>
                  ⚠️ No Business Days Selected
                </div>
                <div style={{ fontSize: '14px', color: '#856404' }}>
                  The selected date range contains only weekends and/or holidays. 
                  Please adjust your dates to include at least one business day.
                </div>
              </div>
            )}

            {/* Off-campus time summary */}
            {isOffCampusOnly && startDate && departureTime && returnTime && (
              <div style={{ 
                marginTop: '15px', 
                padding: '12px', 
                backgroundColor: '#d1ecf1', 
                border: '1px solid #bee5eb', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontWeight: 'bold', color: '#0c5460', marginBottom: '5px' }}>
                  Off-Campus Summary
                </div>
                <div style={{ fontSize: '14px', color: '#0c5460' }}>
                  You will be off-campus on <strong>{startDate}</strong> from <strong>{departureTime}</strong> to <strong>{returnTime}</strong>
                </div>
              </div>
            )}
          </div>

          {/* Reason Selection */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              Reason for {isOffCampusOnly ? 'Being Off-Campus' : 'Absence'}
            </h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Category *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="">-- Select a Reason --</option>
                {(isOffCampusOnly ? offCampusReasonOptions : reasonOptions).map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {reason === 'Other' && (
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  Please Specify Other Reason *
                </label>
                <input
                  type="text"
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  required
                  placeholder={isOffCampusOnly ? "e.g., Doctor's appointment, parent meeting..." : "e.g., Family emergency, medical appointment..."}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context or information..."
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Substitute Coverage Option (only for eligible users and regular absences) */}
          {isEligibleForSubCoverage() && !isOffCampusOnly && (
            <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '16px' }}>
                <input
                  type="checkbox"
                  checked={needsSubCoverage}
                  onChange={(e) => setNeedsSubCoverage(e.target.checked)}
                  style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <strong>I need substitute coverage for this absence</strong>
              </label>
              <p style={{ margin: '8px 0 0 28px', fontSize: '14px', color: '#004085' }}>
                Check this if you'll need a substitute teacher. You'll be redirected to complete a substitute request after submitting this absence.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={() => navigate('/absence-history')}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting || 
                !startDate || 
                (!isOffCampusOnly && !endDate) || 
                !reason || 
                (reason === 'Other' && !reasonOther.trim()) ||
                (!isOffCampusOnly && blackoutWarning && !acknowledgeBlackout)
              }
              style={{
                backgroundColor: (
                  isSubmitting || 
                  !startDate || 
                  (!isOffCampusOnly && !endDate) || 
                  !reason || 
                  (reason === 'Other' && !reasonOther.trim()) ||
                  (!isOffCampusOnly && blackoutWarning && !acknowledgeBlackout)
                ) ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: (
                  isSubmitting || 
                  !startDate || 
                  (!isOffCampusOnly && !endDate) || 
                  !reason || 
                  (reason === 'Other' && !reasonOther.trim()) ||
                  (!isOffCampusOnly && blackoutWarning && !acknowledgeBlackout)
                ) ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Submitting Request...' : (needsSubCoverage ? 'Submit & Continue to Sub Request' : 'Submit Request')}
            </button>
          </div>
        </form>
      </div>

      {/* Help Text */}
      <div style={{ 
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold', color: '#495057' }}>
          Important Information
        </h4>
        <ul style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
          <li>Submit absence requests as early as possible to ensure coverage</li>
          <li>Your supervisor will be notified and will approve or deny the request</li>
          <li>You will receive an email notification when your request is processed</li>
          <li>For emergency absences, contact your supervisor directly by phone</li>
          <li style={{ color: '#856404', fontWeight: 'bold' }}>
            ⚠️ <strong>BLACKOUT DATES:</strong> Absences during blackout periods might result in 
            docked pay, even if approved by your supervisor. You will receive a warning if your 
            request overlaps with a blackout period.
          </li>
          <li><strong>Weekends and school holidays are automatically excluded</strong> from absence requests and do not count toward your absence total</li>
          {isEligibleForSubCoverage() && (
            <li><strong>If you need substitute coverage:</strong> Check the substitute coverage box above to automatically continue to the substitute request form after submission</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default RequestAbsence;